import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { FileSignature, ShieldCheck, CheckCircle2, Mail, Eraser } from "lucide-react";
import { API_BASE_URL } from "../services/api";

// Public, unauthenticated signing page — driven entirely by the signer token.
const pub = axios.create({ baseURL: API_BASE_URL, timeout: 30000 });

export default function SignDocument() {
  const { token } = useParams();
  const [state, setState] = useState("loading"); // loading | ready | signed | done | error
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);

  const [otpSent, setOtpSent] = useState(false);
  const [emailHint, setEmailHint] = useState("");
  const [otp, setOtp] = useState("");
  const [typedName, setTypedName] = useState("");
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [cooldown, setCooldown] = useState(0); // seconds left before a new code can be requested

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setInterval(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const hasDrawn = useRef(false);

  const load = useCallback(() => {
    pub.get(`v1/esignature/sign/${token}`)
      .then((r) => {
        setInfo(r.data);
        if (r.data.already_signed) setState("signed");
        else if (r.data.status === "done") setState("done");
        else setState("ready");
        setTypedName(r.data.signer_name || "");
      })
      .catch((e) => { setError(e.response?.data?.detail || "Lien invalide"); setState("error"); });
  }, [token]);

  useEffect(() => { load(); }, [load]);

  // ── Canvas signature pad ────────────────────────────────────────────────────
  const pos = (e) => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const t = e.touches?.[0];
    return { x: (t ? t.clientX : e.clientX) - rect.left, y: (t ? t.clientY : e.clientY) - rect.top };
  };
  const start = (e) => { drawing.current = true; const { x, y } = pos(e); const ctx = canvasRef.current.getContext("2d"); ctx.beginPath(); ctx.moveTo(x, y); };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const { x, y } = pos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineTo(x, y); ctx.strokeStyle = "#0f172a"; ctx.lineWidth = 2; ctx.lineCap = "round"; ctx.stroke();
    hasDrawn.current = true;
  };
  const end = () => { drawing.current = false; };
  const clearCanvas = () => {
    const c = canvasRef.current;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
    hasDrawn.current = false;
  };

  const requestOtp = async () => {
    if (cooldown > 0) return;
    try {
      const r = await pub.post(`v1/esignature/sign/${token}/otp`);
      setEmailHint(r.data.email_hint || "");
      setOtpSent(true);
      setOtp("");          // a fresh code was generated — clear any stale entry
      setCooldown(60);     // prevent rapid re-sends that invalidate the previous code
    } catch (e) { setError(e.response?.data?.detail || "Échec de l'envoi du code"); }
  };

  const submit = async () => {
    setError(null);
    if (!consent) { setError("Veuillez confirmer votre consentement."); return; }
    if (!otp) { setError("Saisissez le code reçu par email."); return; }
    const signature_image = hasDrawn.current ? canvasRef.current.toDataURL("image/png") : null;
    if (!signature_image && !typedName.trim()) { setError("Dessinez ou saisissez votre signature."); return; }
    setSubmitting(true);
    try {
      await pub.post(`v1/esignature/sign/${token}`, {
        otp, signature_image, typed_name: typedName.trim(), consent: true,
      });
      setState("done");
    } catch (e) { setError(e.response?.data?.detail || "Échec de la signature"); }
    finally { setSubmitting(false); }
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  const Shell = ({ children }) => (
    <div style={{ minHeight: "100vh", background: "#0b1220", display: "flex", justifyContent: "center", padding: "32px 16px" }}>
      <div style={{ width: "100%", maxWidth: 860 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, color: "#e2e8f0", marginBottom: 20 }}>
          <FileSignature size={22} color="#0ea5e9" />
          <span style={{ fontWeight: 700, fontSize: 18 }}>Mawered · Signature électronique</span>
        </div>
        {children}
      </div>
    </div>
  );

  const card = { background: "#111a2e", border: "1px solid #1e293b", borderRadius: 16, padding: 24, color: "#e2e8f0" };

  if (state === "loading") return <Shell><div style={card}>Chargement…</div></Shell>;
  if (state === "error") return <Shell><div style={{ ...card, borderColor: "#ef4444" }}>{error}</div></Shell>;

  if (state === "done" || state === "signed") {
    return (
      <Shell>
        <div style={{ ...card, textAlign: "center", borderColor: "#22c55e" }}>
          <CheckCircle2 size={48} color="#22c55e" style={{ margin: "0 auto 12px" }} />
          <h2 style={{ margin: "0 0 8px" }}>Document signé</h2>
          <p style={{ color: "#94a3b8" }}>
            Merci. Votre signature de « {info?.title} » a bien été enregistrée.
            Vous recevrez une copie du document signé par email.
          </p>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div style={{ ...card, marginBottom: 16 }}>
        <h2 style={{ margin: "0 0 4px" }}>{info?.title}</h2>
        <p style={{ color: "#94a3b8", margin: 0 }}>
          Bonjour {info?.signer_name}, veuillez consulter puis signer ce document.
        </p>
      </div>

      {/* Document preview */}
      <iframe
        title="document"
        src={`${API_BASE_URL}/v1/esignature/sign/${token}/document`}
        style={{ width: "100%", height: 460, border: "1px solid #1e293b", borderRadius: 12, marginBottom: 16, background: "#fff" }}
      />

      {/* Signature pad */}
      <div style={{ ...card, marginBottom: 16 }}>
        <label style={{ fontSize: 13, color: "#94a3b8" }}>Votre signature</label>
        <div style={{ position: "relative", marginTop: 8 }}>
          <canvas
            ref={canvasRef} width={760} height={160}
            onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
            onTouchStart={start} onTouchMove={move} onTouchEnd={end}
            style={{ width: "100%", height: 160, background: "#fff", borderRadius: 10, touchAction: "none", cursor: "crosshair" }}
          />
          <button onClick={clearCanvas}
            style={{ position: "absolute", top: 8, right: 8, display: "flex", alignItems: "center", gap: 4,
              background: "#0b1220", color: "#94a3b8", border: "1px solid #1e293b", borderRadius: 8, padding: "4px 8px", fontSize: 12, cursor: "pointer" }}>
            <Eraser size={12} /> Effacer
          </button>
        </div>
        <div style={{ marginTop: 12 }}>
          <label style={{ fontSize: 13, color: "#94a3b8" }}>… ou tapez votre nom complet</label>
          <input value={typedName} onChange={(e) => setTypedName(e.target.value)}
            style={{ width: "100%", marginTop: 6, padding: "10px 12px", borderRadius: 8, background: "#0b1220",
              border: "1px solid #1e293b", color: "#e2e8f0" }} />
        </div>
      </div>

      {/* OTP + consent */}
      <div style={card}>
        {!otpSent ? (
          <button onClick={requestOtp} disabled={cooldown > 0}
            style={{ display: "flex", alignItems: "center", gap: 8, background: cooldown > 0 ? "#1e293b" : "#0ea5e9", color: "#fff",
              border: "none", borderRadius: 10, padding: "12px 20px", fontWeight: 600,
              cursor: cooldown > 0 ? "not-allowed" : "pointer" }}>
            <Mail size={16} /> {cooldown > 0 ? `Renvoyer dans 0:${String(cooldown).padStart(2, "0")}` : "Recevoir mon code de vérification par email"}
          </button>
        ) : (
          <>
            <p style={{ color: "#94a3b8", fontSize: 13, margin: "0 0 4px" }}>
              Un code à 6 chiffres a été envoyé à {emailHint}. Saisissez-le pour confirmer votre identité.
            </p>
            <p style={{ color: "#f59e0b", fontSize: 12, margin: "0 0 10px" }}>
              ⚠️ Si vous demandez plusieurs codes, seul le <strong>dernier reçu</strong> est valide.
            </p>
            <input value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="123456" inputMode="numeric"
              style={{ width: 160, padding: "10px 12px", borderRadius: 8, background: "#0b1220",
                border: "1px solid #1e293b", color: "#e2e8f0", fontSize: 18, letterSpacing: 4, textAlign: "center" }} />
            <button onClick={requestOtp} disabled={cooldown > 0}
              style={{ marginLeft: 12, background: "none", color: cooldown > 0 ? "#475569" : "#0ea5e9", border: "none",
                cursor: cooldown > 0 ? "not-allowed" : "pointer", fontSize: 13 }}>
              {cooldown > 0 ? `Renvoyer dans 0:${String(cooldown).padStart(2, "0")}` : "Renvoyer"}
            </button>

            <label style={{ display: "flex", alignItems: "flex-start", gap: 8, marginTop: 16, color: "#cbd5e1", fontSize: 13 }}>
              <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} style={{ marginTop: 3 }} />
              <span>
                Je consens à signer électroniquement ce document. Je reconnais que ma signature électronique
                a la même valeur qu'une signature manuscrite et que cette action est horodatée et tracée.
              </span>
            </label>

            {error && <p style={{ color: "#ef4444", fontSize: 13, marginTop: 12 }}>{error}</p>}

            <button onClick={submit} disabled={submitting || !consent}
              style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 16, background: consent ? "#22c55e" : "#1e293b",
                color: "#fff", border: "none", borderRadius: 10, padding: "12px 24px", fontWeight: 600,
                cursor: consent ? "pointer" : "not-allowed", opacity: submitting ? 0.6 : 1 }}>
              <ShieldCheck size={16} /> {submitting ? "Signature en cours…" : "Signer le document"}
            </button>
          </>
        )}
      </div>
    </Shell>
  );
}
